import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, NamedLink, TypographyWrapper } from '../../components';
import {
  addressInvalidError,
  isPurchaseReturnInsuranceError,
  isTransactionInitiateAmountTooLowError,
  isTransactionInitiateListingInsufficientStockError,
  isTransactionInitiateListingNotFoundError,
  transactionInitiateOrderStripeErrors,
} from '../../util/errors';
import { CheckoutPageState } from './CheckoutPage.duck';
import css from './CheckoutError.module.css';
import { TypographyFormat } from '../../components/TypographyWrapper/TypographyWrapper';
import { TAX_ADDRESS_MISMATCH_ERROR_MESSAGE } from '../../util/constants';

interface FormattedErrorProps {
  id: string;
  values?: any;
}

const FormattedError: FC<FormattedErrorProps> = (props: FormattedErrorProps) => {
  const { id, values } = props;

  return (
    <p className={css.failure}>
      <FormattedMessage id={id} values={values} />
    </p>
  );
};

interface FormattedErrorMessageProps {
  message: string;
}

const FormattedErrorMessage: FC<FormattedErrorMessageProps> = (
  props: FormattedErrorMessageProps
) => {
  const { message } = props;

  return <p className={css.failure}>{message}</p>;
};

const CheckoutError: FC = () => {
  const { finishCheckoutError, initiateCheckoutError, submitCheckoutError } = useSelector<any>(
    (state) => state.CheckoutPage
  ) as CheckoutPageState;
  const { handleCardChargeError } = useSelector<any>((state) => state.stripe) as any;

  const isSomeListingNotFound = isTransactionInitiateListingNotFoundError(initiateCheckoutError);
  const isSomeListingInsufficientStock =
    isTransactionInitiateListingInsufficientStockError(initiateCheckoutError);
  const addressValidationError = addressInvalidError(initiateCheckoutError);
  const isAmountTooLow = isTransactionInitiateAmountTooLowError(submitCheckoutError);
  const isReturnInsuranceError = isPurchaseReturnInsuranceError(submitCheckoutError);
  const stripeErrors = transactionInitiateOrderStripeErrors(submitCheckoutError);

  if (isSomeListingNotFound || isSomeListingInsufficientStock) {
    const searchLink = (
      <NamedLink name="SearchPage" to={{ search: '' }}>
        <TypographyWrapper variant="body2" format={TypographyFormat.Underlined}>
          here
        </TypographyWrapper>
      </NamedLink>
    );
    return <FormattedError id="CheckoutPage.listingsNotFoundError" values={{ searchLink }} />;
  }
  if (addressValidationError) {
    return (
      <FormattedError id="CheckoutPage.genericError" values={{ error: addressValidationError }} />
    );
  }
  if (isAmountTooLow) {
    return <FormattedError id="CheckoutPage.initiateOrderAmountTooLow" />;
  }
  if (stripeErrors && stripeErrors.length > 0) {
    // NOTE: Error messages from Stripe are not part of translations. By default they are in
    // English.
    const stripeErrorsAsString = stripeErrors.join(', ');
    return (
      <FormattedError
        id="CheckoutPage.initiateOrderStripeError"
        values={{ stripeErrors: stripeErrorsAsString }}
      />
    );
  }
  if (handleCardChargeError) {
    return (
      <FormattedError
        id="CheckoutPage.initiateOrderStripeError"
        values={{ stripeErrors: handleCardChargeError.message }}
      />
    );
  }
  if (isReturnInsuranceError) {
    return <FormattedError id="CheckoutPage.purchaseReturnAssuranceError" />;
  }
  if (submitCheckoutError || finishCheckoutError || initiateCheckoutError) {
    if (submitCheckoutError?.message?.includes(TAX_ADDRESS_MISMATCH_ERROR_MESSAGE)) {
      return <FormattedErrorMessage message={`${submitCheckoutError.message}`} />;
    }
    return <FormattedError id="CheckoutPage.initiateOrderError" />;
  }

  return null;
};

export default CheckoutError;
